import { TriangleAlert } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AssetBalanceDto, AutoWithdrawalNetworkCode } from '@shared/api/contracts';
import type { AutoWithdrawalRuleDraft } from '@entities/withdrawal';
import { estimateSwapModeFees } from '../../lib/auto-withdraw-fee-estimate';
import styles from './auto-withdraw-ui.module.css';

interface SwapFeesNoticeProps {
  draft: AutoWithdrawalRuleDraft;
  assetBalances: AssetBalanceDto[];
  totalBalance?: number;
  targetNetwork: AutoWithdrawalNetworkCode;
}

export const SwapFeesNotice = ({
  draft,
  assetBalances,
  totalBalance,
  targetNetwork,
}: SwapFeesNoticeProps) => {
  const { t } = useTranslation('cabinet');

  const estimate = useMemo(
    () =>
      estimateSwapModeFees({
        assetBalances,
        totalBalance,
        targetNetwork,
        thresholdAmount: draft.thresholdAmount,
        amountMode: draft.amountMode,
        reserveAmount: draft.reserveAmount,
      }),
    [
      assetBalances,
      draft.amountMode,
      draft.reserveAmount,
      draft.thresholdAmount,
      targetNetwork,
      totalBalance,
    ],
  );

  return (
    <div className={styles.feeAlert} role="note">
      <TriangleAlert className={`${styles.feeAlertIcon} h-4 w-4 shrink-0`} aria-hidden />
      <p className={styles.feeAlertTitle}>{t('withdrawal.auto.single.fees.title')}</p>
      <div className={styles.feeAlertBody}>
        {estimate ? (
          <>
            <ul className={styles.feeList}>
              {estimate.lines.map((line) => (
                <li key={line.labelKey}>
                  <span>
                    {t(`withdrawal.auto.single.fees.lines.${line.labelKey}`, line.labelParams)}
                  </span>
                  <span>{line.value}</span>
                </li>
              ))}
            </ul>
            <div className={styles.feeTotal}>
              <span>{t('withdrawal.auto.single.fees.netReceive')}</span>
              <span>{estimate.netReceive}</span>
            </div>
          </>
        ) : (
          <p className={styles.feeNote}>{t('withdrawal.auto.single.fees.emptyBalance')}</p>
        )}
        <span className={styles.feeNote}>{t('withdrawal.auto.single.fees.note')}</span>
      </div>
    </div>
  );
};
